/**
 * ALTIORA · Revenue Dashboard (Sprint 1)
 *
 * Displays goals, booked commissions, monthly chart, product breakdown
 * and pipeline preview. All data comes from `/api/revenue/dashboard`
 * which is driven by the founder-configurable SalesConfig.
 */
import { useEffect, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, LineChart, Line, Legend,
} from "recharts";
import { RefreshCw, Wallet, Plus, Trash2, Target as TargetIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, Metric, PrimaryButton, GhostButton,
  DangerButton, Modal, FormField, Input, Select, Loader, Table, Badge,
} from "@/components/altiora/PremiumUI";


const MONTH_LABELS = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

const money = (n) => (n || 0).toLocaleString("fr-CA", {
  style: "currency", currency: "CAD", maximumFractionDigits: 0,
});


export default function RevenueDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [year] = useState(new Date().getFullYear());
  const [showGoal, setShowGoal] = useState(false);
  const [showCom, setShowCom] = useState(false);
  const [products, setProducts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, cfgRes] = await Promise.all([
        api.get(`/revenue/dashboard?year=${year}`),
        api.get("/sales_os/config"),
      ]);
      setData(dashRes.data);
      setProducts(cfgRes.data.products || []);
    } catch (err) {
      toast.error("Chargement du tableau de bord impossible");
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const progress = data
    ? Math.min(100, Math.round((data.annual_booked / (data.annual_goal || 1)) * 100))
    : 0;

  return (
    <PageShell testid="revenue-dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 1 · Cœur commercial"
          title={`Revenus & Commissions · ${year}`}
          subtitle="Suivez vos commissions vs objectifs, avec projection annuelle basée sur votre rythme actuel."
          testid="revenue-dashboard-header"
          actions={
            <>
              <GhostButton onClick={load} testid="revenue-refresh">
                <RefreshCw size={13} /> Rafraîchir
              </GhostButton>
              <GhostButton onClick={() => setShowGoal(true)} testid="revenue-set-goal">
                <TargetIcon size={13} /> Fixer un objectif
              </GhostButton>
              <PrimaryButton onClick={() => setShowCom(true)} testid="revenue-add-commission">
                <Plus size={13} /> Ajouter une commission
              </PrimaryButton>
            </>
          }
        />

        {loading && (
          <div className="py-16 flex justify-center"><Loader /></div>
        )}

        {!loading && data && (
          <>
            {/* Metrics row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Metric label="Objectif annuel" value={money(data.annual_goal)}
                      testid="revenue-metric-goal" accent="#94A3B8" />
              <Metric label="Encaissé YTD" value={money(data.annual_booked)}
                      testid="revenue-metric-booked" accent="#16A34A"
                      hint={`${progress}% de l'objectif`} />
              <Metric label="Projection annuelle" value={money(data.annual_forecast)}
                      testid="revenue-metric-forecast" accent="#C5A880" />
              <Metric label="Pipeline (commissions attendues)"
                      value={money(data.pipeline?.expected_pipeline)}
                      hint={`${data.pipeline?.open_deals || 0} opportunités ouvertes`}
                      testid="revenue-metric-pipeline" accent="#0B2545" />
            </div>

            {/* Progress bar */}
            <Card className="p-4 mb-5" testid="revenue-progress-card">
              <div className="flex items-center justify-between text-[11px] mb-2"
                   style={{ color: "#94A3B8" }}>
                <span className="uppercase tracking-widest font-semibold">
                  Progression annuelle
                </span>
                <span>{money(data.annual_booked)} / {money(data.annual_goal)}</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden"
                   style={{ background: "#F1F5F9" }}>
                <div className="h-full transition-all duration-500"
                     style={{ width: `${progress}%`,
                                background: progress >= 100 ? "#16A34A" : "#C5A880" }}
                     data-testid="revenue-progress-bar" />
              </div>
            </Card>

            {/* Chart & product breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
              <Card className="p-4 lg:col-span-2" testid="revenue-chart-card">
                <Section icon={Wallet} title="Commissions mensuelles vs objectif"
                         hint="Comparez chaque mois de l'année en cours">
                  <div style={{ width: "100%", height: 240, minHeight: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.monthly.map(m => ({
                        month: MONTH_LABELS[m.month - 1],
                        booked: m.booked, goal: m.goal, forecast: m.forecast,
                      }))} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#C5A88033" />
                        <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }}
                               tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                        <RTooltip formatter={(v) => money(v)}
                                  labelStyle={{ color: "#0B2545" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="goal"   name="Objectif"  fill="#94A3B855" radius={[4,4,0,0]} />
                        <Bar dataKey="booked" name="Encaissé" fill="#C5A880"    radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              </Card>

              <Card className="p-4" testid="revenue-products-card">
                <Section title="Répartition par produit"
                         hint="Toutes commissions confondues cette année">
                  {data.by_product.length === 0 ? (
                    <div className="text-[12px] italic py-6 text-center"
                         style={{ color: "#94A3B8" }}>
                      Aucune commission encore enregistrée.
                    </div>
                  ) : (
                    <ul className="space-y-2 mt-2">
                      {data.by_product.map((row, i) => {
                        const pct = data.annual_booked
                          ? Math.round((row.total / data.annual_booked) * 100) : 0;
                        return (
                          <li key={row.product_key}
                              data-testid={`revenue-product-row-${i}`}>
                            <div className="flex items-center justify-between text-[12px] mb-1"
                                 style={{ color: "#0B2545" }}>
                              <span>{row.label}</span>
                              <span className="font-semibold">{money(row.total)}</span>
                            </div>
                            <div className="h-1.5 rounded-full"
                                 style={{ background: "#F1F5F9" }}>
                              <div className="h-full rounded-full"
                                   style={{ width: `${pct}%`, background: "#C5A880" }} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Section>
              </Card>
            </div>

            {/* Recent commissions */}
            <Card className="p-4" testid="revenue-recent-card">
              <Section title="Commissions récentes"
                       hint="Les 8 dernières entrées">
                <RecentCommissions rows={data.recent_commissions}
                                   onDeleted={load}
                                   products={products} />
              </Section>
            </Card>
          </>
        )}
      </div>

      {showGoal && (
        <GoalModal year={year} onClose={() => setShowGoal(false)}
                   onSaved={() => { setShowGoal(false); load(); }} />
      )}
      {showCom && (
        <CommissionModal products={products}
                         onClose={() => setShowCom(false)}
                         onCreated={() => { setShowCom(false); load(); }} />
      )}
    </PageShell>
  );
}


function RecentCommissions({ rows, onDeleted, products }) {
  const productLabel = (k) => products.find(p => p.key === k)?.label || k;
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette commission ?")) return;
    try {
      await api.delete(`/revenue/commissions/${id}`);
      toast.success("Commission supprimée");
      onDeleted();
    } catch { toast.error("Suppression impossible"); }
  };

  return (
    <Table
      testid="revenue-recent-table"
      empty="Aucune commission n'a encore été enregistrée cette année."
      columns={[
        { key: "booked_at", label: "Date",
          render: (r) => new Date(r.booked_at).toLocaleDateString("fr-CA") },
        { key: "client_name", label: "Client" },
        { key: "product_key",  label: "Produit",
          render: (r) => productLabel(r.product_key) },
        { key: "kind",         label: "Type",
          render: (r) => <Badge variant={r.kind === "trailing" ? "info" : "gold"} size="sm">
            {r.kind}
          </Badge> },
        { key: "amount",       label: "Montant",
          render: (r) => <span style={{ fontWeight: 600 }}>{money(r.amount)}</span> },
        { key: "actions",      label: "",
          render: (r) => (
            <button onClick={() => remove(r.id)} aria-label="Supprimer"
                    data-testid={`revenue-delete-${r.id}`}
                    className="p-1 rounded hover:bg-slate-100"
                    style={{ color: "#94A3B8" }}>
              <Trash2 size={13} />
            </button>
          ) },
      ]}
      rows={rows}
    />
  );
}


function GoalModal({ year, onClose, onSaved }) {
  const [form, setForm] = useState({
    period: "annual", year, month: new Date().getMonth() + 1, target_amount: 100000,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/revenue/goals", {
        period: form.period,
        year: parseInt(form.year, 10),
        month: form.period === "monthly" ? parseInt(form.month, 10) : null,
        target_amount: parseFloat(form.target_amount) || 0,
      });
      toast.success("Objectif enregistré");
      onSaved();
    } catch { toast.error("Impossible d'enregistrer l'objectif"); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} testid="revenue-goal-modal"
           title="Fixer un objectif de revenus"
           description="Un objectif annuel + des objectifs mensuels facultatifs."
           footer={
             <>
               <GhostButton onClick={onClose} testid="revenue-goal-cancel">Annuler</GhostButton>
               <PrimaryButton onClick={submit} disabled={saving}
                              testid="revenue-goal-save">
                 {saving ? "Enregistrement…" : "Enregistrer"}
               </PrimaryButton>
             </>
           }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Période" testid="revenue-goal-period">
          {({ id }) => (
            <Select id={id} value={form.period}
                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                    testid="revenue-goal-period-select">
              <option value="annual">Annuel</option>
              <option value="monthly">Mensuel</option>
            </Select>
          )}
        </FormField>
        <FormField label="Année" testid="revenue-goal-year">
          {({ id }) => (
            <Input id={id} type="number" value={form.year}
                   onChange={(e) => setForm({ ...form, year: e.target.value })}
                   testid="revenue-goal-year-input" />
          )}
        </FormField>
        {form.period === "monthly" && (
          <FormField label="Mois" testid="revenue-goal-month">
            {({ id }) => (
              <Select id={id} value={form.month}
                      onChange={(e) => setForm({ ...form, month: e.target.value })}
                      testid="revenue-goal-month-select">
                {MONTH_LABELS.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </Select>
            )}
          </FormField>
        )}
        <FormField label="Montant cible ($)" required testid="revenue-goal-amount">
          {({ id }) => (
            <Input id={id} type="number" min="0" step="100"
                   value={form.target_amount}
                   onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                   testid="revenue-goal-amount-input" />
          )}
        </FormField>
      </div>
    </Modal>
  );
}


function CommissionModal({ products, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", product_key: products[0]?.key || "",
    amount: "", kind: "first_year",
    booked_at: new Date().toISOString().slice(0, 10), notes: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.client_name.trim() || !form.product_key || !form.amount) {
      toast.error("Client, produit et montant sont requis"); return;
    }
    setSaving(true);
    try {
      await api.post("/revenue/commissions", {
        client_name: form.client_name.trim(),
        product_key: form.product_key,
        amount: parseFloat(form.amount),
        kind: form.kind,
        booked_at: form.booked_at,
        notes: form.notes.trim() || null,
      });
      toast.success("Commission ajoutée");
      onCreated();
    } catch { toast.error("Impossible d'ajouter la commission"); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} testid="revenue-com-modal"
           title="Ajouter une commission"
           description="Saisie manuelle. Les commissions sont automatiquement créées lorsqu'une opportunité passe en « Émis »."
           footer={
             <>
               <GhostButton onClick={onClose} testid="revenue-com-cancel">Annuler</GhostButton>
               <PrimaryButton onClick={submit} disabled={saving}
                              testid="revenue-com-save">
                 {saving ? "Enregistrement…" : "Enregistrer"}
               </PrimaryButton>
             </>
           }>
      <FormField label="Client" required testid="revenue-com-client">
        {({ id }) => (
          <Input id={id} value={form.client_name}
                 onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                 testid="revenue-com-client-input" />
        )}
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Produit" required testid="revenue-com-product">
          {({ id }) => (
            <Select id={id} value={form.product_key}
                    onChange={(e) => setForm({ ...form, product_key: e.target.value })}
                    testid="revenue-com-product-select">
              {products.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label="Montant ($)" required testid="revenue-com-amount">
          {({ id }) => (
            <Input id={id} type="number" step="0.01"
                   value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   testid="revenue-com-amount-input" />
          )}
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Type" testid="revenue-com-kind">
          {({ id }) => (
            <Select id={id} value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    testid="revenue-com-kind-select">
              <option value="first_year">Première année</option>
              <option value="trailing">Renouvellement</option>
              <option value="override">Override</option>
              <option value="adjustment">Ajustement</option>
            </Select>
          )}
        </FormField>
        <FormField label="Date" required testid="revenue-com-date">
          {({ id }) => (
            <Input id={id} type="date" value={form.booked_at}
                   onChange={(e) => setForm({ ...form, booked_at: e.target.value })}
                   testid="revenue-com-date-input" />
          )}
        </FormField>
      </div>
    </Modal>
  );
}
